<template>
  <div class="account">
    <div class="container">
      <h1 class="mb-4">My Account</h1>
      
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-else class="row g-4">
        <!-- Account Information -->
        <div class="col-lg-8">
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h3 class="h5 mb-0">Account Information</h3>
              <button 
                class="btn btn-sm btn-outline-primary" 
                @click="isEditingProfile = !isEditingProfile"
              >
                {{ isEditingProfile ? 'Cancel' : 'Edit' }}
              </button>
            </div>
            <div class="card-body p-4">
              <form v-if="isEditingProfile" @submit.prevent="updateProfile">
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label for="name" class="form-label">Full Name</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="name" 
                      v-model="profile.name"
                      required
                    >
                  </div>
                  <div class="col-md-6">
                    <label for="email" class="form-label">Email Address</label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email" 
                      v-model="profile.email"
                      required
                    >
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label for="phone" class="form-label">Phone Number</label>
                    <input 
                      type="tel" 
                      class="form-control" 
                      id="phone" 
                      v-model="profile.phone"
                      required
                    >
                  </div>
                  <div class="col-md-6">
                    <label for="address" class="form-label">Address</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="address" 
                      v-model="profile.address"
                    >
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <label for="city" class="form-label">City</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="city" 
                      v-model="profile.city"
                    >
                  </div>
                  <div class="col-md-3">
                    <label for="state" class="form-label">State</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="state" 
                      v-model="profile.state"
                    >
                  </div>
                  <div class="col-md-3">
                    <label for="zip" class="form-label">ZIP Code</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="zip" 
                      v-model="profile.zip"
                    >
                  </div>
                </div>
                <button 
                  type="submit" 
                  class="btn btn-primary"
                  :disabled="profileLoading"
                >
                  <span v-if="profileLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Save Changes
                </button>
              </form>
              
              <div v-else>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Account Number:</strong></p>
                    <p>{{ accountInfo?.accountNumber }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Status:</strong></p>
                    <p>
                      <span 
                        class="badge"
                        :class="{
                          'bg-success': accountInfo?.status === 'Active',
                          'bg-warning': accountInfo?.status === 'Pending',
                          'bg-danger': accountInfo?.status === 'Suspended'
                        }"
                      >
                        {{ accountInfo?.status }}
                      </span>
                    </p>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Full Name:</strong></p>
                    <p>{{ accountInfo?.name }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Email Address:</strong></p>
                    <p>{{ accountInfo?.email }}</p>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Phone Number:</strong></p>
                    <p>{{ accountInfo?.phone }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Billing Address:</strong></p>
                    <p>
                      {{ accountInfo?.address }}<br>
                      {{ accountInfo?.city }}, {{ accountInfo?.state }} {{ accountInfo?.zip }}
                    </p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Customer Since:</strong></p>
                    <p>{{ formatDate(accountInfo?.createdAt) }}</p>
                  </div>
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Billing Cycle:</strong></p>
                    <p>{{ formatDate(accountInfo?.billingCycleStart) }} - {{ formatDate(accountInfo?.billingCycleEnd) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Current Plan -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h3 class="h5 mb-0">Current Plan</h3>
              <button 
                class="btn btn-sm btn-outline-primary" 
                @click="isChangingPlan = !isChangingPlan"
              >
                {{ isChangingPlan ? 'Cancel' : 'Change Plan' }}
              </button>
            </div>
            <div class="card-body p-4">
              <div v-if="isChangingPlan">
                <div class="row g-4">
                  <div v-for="plan in plans" :key="plan.id" class="col-md-4">
                    <div 
                      class="card h-100 border-0 shadow-sm"
                      :class="{ 'border border-primary': plan.id === accountInfo?.plan?.id }"
                    >
                      <div class="card-header bg-primary text-white text-center py-3">
                        <h3 class="m-0">{{ plan.name }}</h3>
                        <span 
                          v-if="plan.id === accountInfo?.plan?.id" 
                          class="badge bg-warning text-dark position-absolute top-0 start-50 translate-middle"
                        >
                          Current Plan
                        </span>
                      </div>
                      <div class="card-body text-center p-4">
                        <h4 class="card-price">${{ plan.price }}<span class="period">/month</span></h4>
                        <ul class="list-unstyled mt-3 mb-4">
                          <li>{{ isUnlimited(plan.data) ? 'Unlimited' : formatData(plan.data) }} Data</li>
                          <li>{{ isUnlimited(plan.minutes) ? 'Unlimited' : plan.minutes }} Minutes</li>
                          <li>{{ isUnlimited(plan.sms) ? 'Unlimited' : plan.sms }} SMS</li>
                          <li>{{ plan.features.join(', ') }}</li>
                        </ul>
                        <button 
                          class="btn"
                          :class="{
                            'btn-primary': plan.id !== accountInfo?.plan?.id,
                            'btn-outline-primary': plan.id === accountInfo?.plan?.id
                          }"
                          :disabled="plan.id === accountInfo?.plan?.id || planLoading"
                          @click="changePlan(plan.id)"
                        >
                          <span v-if="planLoading && selectedPlanId === plan.id" class="spinner-border spinner-border-sm me-2" role="status"></span>
                          {{ plan.id === accountInfo?.plan?.id ? 'Current Plan' : 'Select Plan' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-else>
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4>{{ accountInfo?.plan?.name }}</h4>
                    <p class="mb-0">Monthly Cost: {{ formatCurrency(accountInfo?.plan?.price) }}</p>
                    <ul class="mt-3">
                      <li>{{ isUnlimited(accountInfo?.plan?.data) ? 'Unlimited' : formatData(accountInfo?.plan?.data) }} Data</li>
                      <li>{{ isUnlimited(accountInfo?.plan?.minutes) ? 'Unlimited' : accountInfo?.plan?.minutes }} Minutes</li>
                      <li>{{ isUnlimited(accountInfo?.plan?.sms) ? 'Unlimited' : accountInfo?.plan?.sms }} SMS</li>
                      <li v-for="(feature, index) in accountInfo?.plan?.features" :key="index">
                        {{ feature }}
                      </li>
                    </ul>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <span 
                      class="badge bg-success mb-2"
                      v-if="accountInfo?.autoRenew"
                    >
                      Auto Renew Enabled
                    </span>
                    <p class="text-muted small">
                      Next billing date: {{ formatDate(accountInfo?.billingCycleEnd) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Payment Methods -->
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-transparent border-0">
              <h3 class="h5 mb-0">Payment Methods</h3>
            </div>
            <div class="card-body p-4">
              <div class="d-flex align-items-center mb-3">
                <div class="me-3">
                  <i class="bi bi-credit-card fs-3"></i>
                </div>
                <div>
                  <p class="mb-0 fw-bold">Credit Card ending in 1234</p>
                  <p class="text-muted small mb-0">Expires 05/2025</p>
                </div>
                <div class="ms-auto">
                  <span class="badge bg-success">Default</span>
                </div>
              </div>
              <button class="btn btn-outline-primary w-100 mt-3">
                <i class="bi bi-plus-circle"></i> Add Payment Method
              </button>
            </div>
          </div>
          
          <!-- Auto Pay -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-transparent border-0">
              <h3 class="h5 mb-0">Auto Pay</h3>
            </div>
            <div class="card-body p-4">
              <div class="form-check form-switch">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="autoPay"
                  v-model="autoPay"
                  @change="toggleAutoPay"
                >
                <label class="form-check-label" for="autoPay">
                  Enable Automatic Payments
                </label>
              </div>
              <p class="text-muted small mt-2">
                When enabled, your default payment method will be automatically charged on the billing date.
              </p>
            </div>
          </div>
          
          <!-- Security -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-transparent border-0">
              <h3 class="h5 mb-0">Security</h3>
            </div>
            <div class="card-body p-4">
              <button class="btn btn-outline-primary w-100 mb-2">
                Change Password
              </button>
              <button class="btn btn-outline-primary w-100">
                Two-Factor Authentication
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'AccountView',
  setup() {
    const store = useStore()
    
    // State
    const loading = ref(true)
    const error = ref(null)
    const isEditingProfile = ref(false)
    const isChangingPlan = ref(false)
    const profileLoading = ref(false)
    const planLoading = ref(false)
    const selectedPlanId = ref(null)
    const autoPay = ref(false)
    
    const profile = reactive({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    })
    
    // Computed properties
    const accountInfo = computed(() => store.getters['account/accountInfo'])
    const plans = computed(() => store.getters['account/plans'])
    
    // Methods
    const isUnlimited = (value) => {
      return value === -1 || value === 'unlimited' || value === null
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    }
    
    const formatData = (bytes) => {
      if (!bytes || bytes === 0) return '0 GB'
      if (isUnlimited(bytes)) return 'Unlimited'
      const gigabytes = bytes / (1024 * 1024 * 1024)
      return gigabytes.toFixed(2) + ' GB'
    }
    
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    }
    
    const initProfile = () => {
      if (accountInfo.value) {
        profile.name = accountInfo.value.name || ''
        profile.email = accountInfo.value.email || ''
        profile.phone = accountInfo.value.phone || ''
        profile.address = accountInfo.value.address || ''
        profile.city = accountInfo.value.city || ''
        profile.state = accountInfo.value.state || ''
        profile.zip = accountInfo.value.zip || ''
        autoPay.value = accountInfo.value.autoRenew || false
      }
    }
    
    const updateProfile = async () => {
      try {
        profileLoading.value = true
        await store.dispatch('account/updateProfile', profile)
        isEditingProfile.value = false
      } catch (err) {
        error.value = 'Failed to update profile. Please try again.'
        console.error(err)
      } finally {
        profileLoading.value = false
      }
    }
    
    const changePlan = async (planId) => {
      try {
        planLoading.value = true
        selectedPlanId.value = planId
        await store.dispatch('account/changePlan', planId)
        isChangingPlan.value = false
      } catch (err) {
        error.value = 'Failed to change plan. Please try again.'
        console.error(err)
      } finally {
        planLoading.value = false
        selectedPlanId.value = null
      }
    }
    
    const toggleAutoPay = async () => {
      try {
        const payload = {
          autoRenew: autoPay.value
        }
        await store.dispatch('account/updateProfile', payload)
      } catch (err) {
        error.value = 'Failed to update auto-pay settings. Please try again.'
        console.error(err)
        // Revert the toggle if the update failed
        autoPay.value = !autoPay.value
      }
    }
    
    // Fetch data on component mount
    onMounted(async () => {
      try {
        loading.value = true
        await Promise.all([
          store.dispatch('account/fetchAccountInfo'),
          store.dispatch('account/fetchPlans')
        ])
        initProfile()
      } catch (err) {
        error.value = 'Failed to load account information. Please try again.'
        console.error(err)
      } finally {
        loading.value = false
      }
    })
    
    return {
      loading,
      error,
      accountInfo,
      plans,
      profile,
      isEditingProfile,
      isChangingPlan,
      profileLoading,
      planLoading,
      selectedPlanId,
      autoPay,
      isUnlimited,
      formatDate,
      formatData,
      formatCurrency,
      updateProfile,
      changePlan,
      toggleAutoPay
    }
  }
}
</script>

<style scoped>
.account {
  padding: 20px 0;
}

.card-price {
  font-size: 2.5rem;
  font-weight: bold;
}

.period {
  font-size: 1rem;
  font-weight: normal;
}
</style>